import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { ThemeProvider } from '@/components/shared/ThemeProvider'
import { Button } from '@/components/ui/button'
import { Check, Search, ArrowRight, ArrowLeft, BookOpen, Trophy, Award } from 'lucide-react'
import type { PageProps } from '@/types'

interface ExamOption {
  id: number
  name: string
  code: string
  year: number | null
  tests_count: number
}

interface Props {
  exams: ExamOption[]
  enrolled_exam_ids: number[]
}

export default function OnboardingWelcome({ exams = [], enrolled_exam_ids = [] }: Props) {
  const { auth } = usePage<PageProps>().props
  const [step, setStep] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')

  // Only consider exams that actually have more than 0 tests available
  const availableExams = exams.filter(e => e.tests_count > 0)
  const hasAvailableExams = availableExams.length > 0

  const [selectedIds, setSelectedIds] = useState<number[]>(() =>
    enrolled_exam_ids.filter(id => availableExams.some(e => e.id === id))
  )
  const [submitting, setSubmitting] = useState(false)

  const toggleExam = (id: number) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const handleComplete = (idsToSubmit = selectedIds) => {
    setSubmitting(true)
    router.patch('/student/onboarding', { exam_ids: idsToSubmit })
  }

  const filteredExams = availableExams.filter(exam =>
    exam.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exam.code.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const studentName = auth.user?.name?.split(' ')[0] || 'Student'
  const totalSteps = 2

  return (
    <ThemeProvider>
      <div className="min-h-screen min-h-[100dvh] bg-background flex flex-col justify-center items-center p-3 sm:p-4 py-4 sm:py-8 overflow-y-auto">
        <div className="w-full max-w-sm sm:max-w-md my-auto">
          <div className="bg-card rounded-3xl shadow-xl border border-border/70 p-5 sm:p-7">
            {/* Step 1: Welcome Screen (Mockup 1) */}
            {step === 0 && (
              <div className="space-y-5 sm:space-y-6">
                {/* 4-segment Progress Bar at top of card */}
                <div className="flex gap-1.5 sm:gap-2 w-full">
                  {Array.from({ length: totalSteps }).map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                        i === 0 ? 'bg-primary' : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>

                {/* Hero Icon */}
                <div>
                  <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl bg-primary/15 flex items-center justify-center text-primary">
                    <BookOpen className="h-6 w-6 sm:h-7 sm:w-7" />
                  </div>
                </div>

                {/* Title & Subtitle */}
                <div className="space-y-1">
                  <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                    Welcome to PrepKiya, {studentName}
                  </h1>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Let&apos;s set up your prep in under a minute.
                  </p>
                </div>

                {/* Feature List with clean divider lines */}
                <div className="divide-y divide-border/60 border-y border-border/60">
                  {[
                    { icon: BookOpen, text: 'Full mock tests and sectional drills' },
                    { icon: Trophy, text: 'Live leaderboards to track your rank' },
                    { icon: Award, text: 'Badges and credits as you practice' },
                  ].map(({ icon: Icon, text }) => (
                    <div key={text} className="flex items-center gap-3.5 py-3 sm:py-3.5">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary shrink-0">
                        <Icon className="h-4 w-4 sm:h-4.5 sm:w-4.5" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-foreground leading-tight">
                        {text}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Primary Action Button */}
                <Button
                  size="lg"
                  className="w-full h-11 sm:h-12 text-sm sm:text-base font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all cursor-pointer shadow-sm"
                  disabled={submitting}
                  onClick={hasAvailableExams ? () => setStep(1) : () => handleComplete([])}
                >
                  {submitting ? (
                    'Setting up...'
                  ) : (
                    <span className="flex items-center justify-center gap-1.5">
                      Get started <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </div>
            )}

            {/* Step 2: Choose Your Target Exams (Mockup 2) */}
            {step === 1 && hasAvailableExams && (
              <div className="space-y-4 sm:space-y-5">
                {/* Header with Circular Back Button & Step Counter */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => setStep(0)}
                      className="w-8 h-8 rounded-full border border-border/80 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shrink-0 cursor-pointer"
                      aria-label="Go back"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Step 2 of {totalSteps}
                    </span>
                  </div>

                  {/* 4-segment Progress Bar */}
                  <div className="flex gap-1.5 sm:gap-2 w-full">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          i <= 1 ? 'bg-primary' : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Title & Subtitle */}
                <div className="space-y-1">
                  <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                    Choose your target exams
                  </h1>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Select all exams you&apos;re preparing for. You can change this anytime.
                  </p>
                </div>

                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search exams"
                    className="w-full pl-10 pr-4 py-2.5 rounded-full border border-border bg-background text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>

                {/* Section Header */}
                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider pt-0.5">
                  Available Now
                </div>

                {/* Exam List */}
                <div className="space-y-2 sm:space-y-2.5 max-h-[36vh] sm:max-h-[42vh] overflow-y-auto pr-0.5">
                  {filteredExams.length === 0 ? (
                    <div className="text-center py-6 text-xs sm:text-sm text-muted-foreground">
                      No exams matching &ldquo;{searchQuery}&rdquo;
                    </div>
                  ) : (
                    filteredExams.map(exam => {
                      const selected = selectedIds.includes(exam.id)
                      return (
                        <button
                          key={exam.id}
                          type="button"
                          onClick={() => toggleExam(exam.id)}
                          className={`w-full text-left p-3.5 sm:p-4 rounded-2xl border-2 transition-all flex items-center justify-between gap-3 select-none cursor-pointer ${
                            selected
                              ? 'border-primary/70 bg-primary/10 shadow-sm'
                              : 'border-border hover:border-primary/40 bg-card'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <span className="font-semibold text-xs sm:text-sm text-foreground truncate block">
                              {exam.name}
                            </span>
                            <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">
                              {exam.tests_count} {exam.tests_count === 1 ? 'test' : 'tests'} available
                            </p>
                          </div>
                          <div
                            className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 border transition-all ${
                              selected
                                ? 'bg-primary border-primary text-primary-foreground shadow-sm'
                                : 'border-border/80 bg-background/60'
                            }`}
                          >
                            {selected && <Check className="h-3.5 w-3.5 stroke-[2.5]" />}
                          </div>
                        </button>
                      )
                    })
                  )}

                  {/* More exams added weekly dashed banner */}
                  <div className="border border-dashed border-border/80 rounded-2xl py-3 text-center text-xs text-muted-foreground font-medium bg-muted/20">
                    More exams added weekly
                  </div>
                </div>

                {/* Primary CTA Button */}
                <Button
                  size="lg"
                  className="w-full h-11 sm:h-12 text-sm font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all cursor-pointer shadow-sm"
                  disabled={selectedIds.length === 0 || submitting}
                  onClick={() => handleComplete(selectedIds)}
                >
                  {submitting ? (
                    'Setting up...'
                  ) : selectedIds.length === 0 ? (
                    'Select an exam to continue'
                  ) : (
                    `Continue with ${selectedIds.length} ${selectedIds.length === 1 ? 'exam' : 'exams'}`
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </ThemeProvider>
  )
}
